import { beginMessage } from '../message';
import { MessageTag } from '../messageTags';
import { TicketStatus, TicketType } from '../enums';
import type { BinaryReader } from '../BinaryReader';

/**
 * Support-ticket codec. Mirrors GetTicketHandler (server) and TicketPage.cs (Unity).
 */

/** C2S Ticket(9): empty body — fetch the user's open ticket + recent games/transactions/paybacks. */
export function encodeTicketRequest(): Uint8Array {
  return beginMessage(MessageTag.Ticket).toUint8Array();
}

export interface TicketState {
  status: TicketStatus;
  /** subject/body/answer are present only when status != Empty (in practice only when New). */
  subject: string;
  body: string;
  answer: string;
  /** Pre-rendered display rows for the reference dropdowns (each up to 10). */
  lastGames: string[];
  lastTransactions: string[];
  lastPaybacks: string[];
  /**
   * One flat long[] = [GAME_RESULT_IDs..][TRANSACTION_IDs..][PAYBACK_IDs..] concatenated.
   * Index into the correct block when building a TicketSave referenceId.
   */
  referenceIds: bigint[];
}

/**
 * S2C Ticket(9): short status; if status != Empty then string subject/body/answer;
 * then ALWAYS string[] lastGames, string[] lastTransactions, string[] lastPaybacks, long[] referenceIds.
 */
export function decodeTicket(r: BinaryReader): TicketState {
  const status = r.getShort() as TicketStatus;
  let subject = '';
  let body = '';
  let answer = '';
  if (status !== TicketStatus.Empty) {
    subject = r.getString();
    body = r.getString();
    answer = r.getString();
  }
  return {
    status,
    subject,
    body,
    answer,
    lastGames: r.getStringArray(),
    lastTransactions: r.getStringArray(),
    lastPaybacks: r.getStringArray(),
    referenceIds: r.getLongArray(),
  };
}

/**
 * C2S TicketSave(51): string subject, string body, string ticketType (the enum NAME, e.g. "General"),
 * long referenceId (0 = none). The Unity client always sends all four.
 */
export function encodeTicketSave(
  subject: string,
  body: string,
  ticketType: TicketType,
  referenceId: bigint,
): Uint8Array {
  return beginMessage(MessageTag.TicketSave)
    .putString(subject)
    .putString(body)
    .putString(TicketType[ticketType]) // enum name string: General | Game | Transaction | Payback
    .putLong(referenceId)
    .toUint8Array();
}
